import { Toast as MessageApi } from 'antd-mobile';
import { REQUEST_HOST } from './constant';

// 请求配置接口
interface RequestConfig {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: Record<string, any>;
  headers?: Record<string, string>;
  timeout?: number;
  withCredentials?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// 错误信息接口
interface ErrorInfo {
  type: string;
  message: string;
  detail?: string;
  code?: string;
  status?: number;
  url?: string;
  isNetworkError?: boolean;
  isCorsError?: boolean;
  isTimeout?: boolean;
}

// 默认配置
const DEFAULT_CONFIG = {
  timeout: 30000,
  withCredentials: true,
  baseURL: REQUEST_HOST,
};

// 创建带超时的 fetch
function fetchWithTimeout(url: string, options: RequestInit, timeout: number): Promise<Response> {
  return Promise.race([
    fetch(url, options),
    new Promise<Response>((_, reject) =>
      setTimeout(() => reject(new Error('请求超时')), timeout)
    ),
  ]) as Promise<Response>;
}

// 构建完整的 URL
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildURL(url: string, params?: Record<string, any>): string {
  const baseURL = DEFAULT_CONFIG.baseURL;
  const fullURL = url.startsWith('http') ? url : `${baseURL}${url}`;
  
  if (!params || Object.keys(params).length === 0) {
    return fullURL;
  }

  const urlObj = new URL(fullURL);
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      urlObj.searchParams.append(key, String(value));
    }
  });

  return urlObj.toString();
}

// 处理响应数据
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleResponse(response: Response): Promise<any> {
  const contentType = response.headers.get('content-type');
  const isJson = contentType && contentType.includes('application/json');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let responseData: any;
  if (isJson) {
    responseData = await response.json();
  } else {
    responseData = await response.text();
  }

  // 如果响应数据不存在或不是对象（包括数组），直接返回
  if (!responseData || typeof responseData !== 'object' || Array.isArray(responseData)) {
    return responseData;
  }

  // 从响应数据中获取 code、msg、message
  const code = responseData.code;
  const msg = responseData.msg;
  const message = responseData.message;

  // 兼容后端字段值不规范情况
  const responseMsg = msg || message;

  // 只有当 code 存在且不等于 200 和 0 时才 reject
  if (code !== undefined && code !== null && +code !== 200 && +code !== 0) {
    return Promise.reject(responseMsg || '请求失败');
  }

  return responseData;
}

// 处理错误
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleError(error: any, url: string): ErrorInfo {
  const errorInfo: ErrorInfo = {
    type: 'unknown',
    message: '服务器异常',
  };

  // 检查是否是超时错误
  if (error.message === '请求超时' || error.message?.includes('timeout')) {
    errorInfo.type = 'timeout_error';
    errorInfo.isTimeout = true;
    errorInfo.message = '请求超时，请检查网络是否异常';
    return errorInfo;
  }

  // 检查是否是网络错误（fetch 失败）
  // 这种情况通常发生在：
  // 1. CORS 预检请求失败
  // 2. 响应头中缺少 CORS 头，浏览器阻止了响应
  // 3. 网络连接问题
  if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
    errorInfo.type = 'network_error';
    errorInfo.isNetworkError = true;
    errorInfo.isCorsError = true;
    errorInfo.message = '网络请求失败，可能是跨域问题';
    errorInfo.detail = `错误信息: ${error.message}\n\n如果抓包工具能看到响应数据，但浏览器报错，说明是 CORS 配置问题：\n1. 检查服务器是否返回了正确的 CORS 响应头\n2. 如果使用 credentials，Access-Control-Allow-Origin 不能是 *\n3. 检查 OPTIONS 预检请求是否成功\n4. 查看浏览器 Network 标签中的响应头`;
    errorInfo.url = url;
    return errorInfo;
  }

  // 其他错误
  errorInfo.type = 'unknown_error';
  errorInfo.message = error.message || '未知错误';
  errorInfo.detail = error.stack || '';
  errorInfo.url = url;

  return errorInfo;
}

// 处理 HTTP 错误响应
function handleHttpError(response: Response, url: string): ErrorInfo {
  const errorInfo: ErrorInfo = {
    type: 'http_error',
    status: response.status,
    message: `请求失败 (${response.status})`,
    detail: response.statusText || `HTTP ${response.status} 错误`,
    url,
  };

  // 根据状态码提供更具体的错误信息
  if (response.status === 404) {
    errorInfo.message = '请求的资源不存在 (404)';
  } else if (response.status === 403) {
    errorInfo.message = '没有权限访问 (403)';
  } else if (response.status === 401) {
    errorInfo.message = '未授权，请重新登录 (401)';
  } else if (response.status >= 500) {
    errorInfo.message = '服务器内部错误 (500+)';
  }

  return errorInfo;
}

// 核心请求函数
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function request<T = any>(
  method: string,
  url: string,
  config: RequestConfig = {}
): Promise<T> {
  const {
    params,
    headers = {},
    timeout = DEFAULT_CONFIG.timeout,
    withCredentials = DEFAULT_CONFIG.withCredentials,
    ...restConfig
  } = config;

  // 构建完整 URL
  const fullURL = buildURL(url, params);

  // 构建请求选项
  const requestOptions: RequestInit = {
    method: method.toUpperCase(),
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': 'zh-CN,zh;q=0.9',
      ...headers,
    },
    credentials: withCredentials ? 'include' : 'same-origin',
    ...restConfig,
  };

  // 输出请求信息（用于调试）
  console.group('📤 发送请求');
  console.log('请求方法:', method.toUpperCase());
  console.log('请求 URL:', fullURL);
  console.log('请求头:', requestOptions.headers);
  console.log('请求配置:', { timeout, withCredentials });
  console.groupEnd();

  try {
    // 发送请求（带超时）
    const response = await fetchWithTimeout(fullURL, requestOptions, timeout);

    // 输出响应信息（用于调试）
    console.group('📥 收到响应');
    console.log('响应状态码:', response.status);
    console.log('响应状态文本:', response.statusText);
    console.log('响应头:', Object.fromEntries(response.headers.entries()));
    console.log('响应 URL:', response.url);
    console.log('响应类型:', response.type);
    console.log('响应是否成功:', response.ok);
    console.groupEnd();

    // 检查 HTTP 状态码
    if (!response.ok) {
      // 尝试读取响应内容
      let responseBody = '';
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          responseBody = JSON.stringify(await response.json());
        } else {
          responseBody = await response.text();
        }
      } catch (e) {
        responseBody = '无法读取响应内容';
      }

      const errorInfo = handleHttpError(response, fullURL);
      errorInfo.detail = `${errorInfo.detail}\n响应内容: ${responseBody}`;
      
      // 在开发环境下输出详细的错误信息到控制台
      console.group('🚨 请求错误详情');
      console.error('错误类型:', errorInfo.type);
      console.error('错误信息:', errorInfo.message);
      if (errorInfo.detail) {
        console.error('详细信息:', errorInfo.detail);
      }
      if (errorInfo.status) {
        console.error('HTTP 状态码:', errorInfo.status);
      }
      if (errorInfo.url) {
        console.error('请求 URL:', errorInfo.url);
      }
      console.error('响应内容:', responseBody);
      console.groupEnd();

      // 显示用户友好的错误提示
      MessageApi.show({
        content: errorInfo.message,
        duration: 2,
      });

      return Promise.reject(errorInfo);
    }

    // 处理响应数据
    const data = await handleResponse(response);
    console.log('✅ 请求成功，响应数据:', data);
    return data as T;
  } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    // 处理请求错误
    const errorInfo = handleError(error, fullURL);

    // 在开发环境下输出详细的错误信息到控制台
    console.group('🚨 请求错误详情');
    console.error('错误类型:', errorInfo.type);
    console.error('错误信息:', errorInfo.message);
    console.error('错误名称:', error.name);
    console.error('错误消息:', error.message);
    if (errorInfo.detail) {
      console.error('详细信息:', errorInfo.detail);
    }
    if (errorInfo.code) {
      console.error('错误代码:', errorInfo.code);
    }
    if (errorInfo.status) {
      console.error('HTTP 状态码:', errorInfo.status);
    }
    if (errorInfo.url) {
      console.error('请求 URL:', errorInfo.url);
    }
    if (errorInfo.isCorsError) {
      console.warn('⚠️ 这很可能是跨域问题（CORS），请检查：');
      console.warn('  1. 服务器是否返回了正确的 CORS 响应头：');
      console.warn('     - Access-Control-Allow-Origin: * 或具体域名');
      console.warn('     - Access-Control-Allow-Credentials: true (如果使用 credentials)');
      console.warn('     - Access-Control-Allow-Headers: 包含你使用的请求头');
      console.warn('     - Access-Control-Allow-Methods: 包含你使用的请求方法');
      console.warn('  2. 如果使用 withCredentials=true，Access-Control-Allow-Origin 不能是 *');
      console.warn('  3. 检查浏览器控制台的 Network 标签，查看响应头');
      console.warn('  4. 抓包工具能看到响应，但浏览器阻止了，说明是 CORS 配置问题');
    }
    if (error.stack) {
      console.error('错误堆栈:', error.stack);
    }
    console.error('完整错误对象:', error);
    console.groupEnd();

    // 显示用户友好的错误提示
    MessageApi.show({
      content: errorInfo.message,
      duration: 2,
    });

    return Promise.reject(errorInfo);
  }
}

// 创建请求实例对象
const fetchRequest = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get<T = any>(url: string, config: RequestConfig = {}): Promise<T> {
    return request<T>('GET', url, config);
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<T> {
    return request<T>('POST', url, {
      ...config,
      body: JSON.stringify(data),
    });
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put<T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<T> {
    return request<T>('PUT', url, {
      ...config,
      body: JSON.stringify(data),
    });
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete<T = any>(url: string, config: RequestConfig = {}): Promise<T> {
    return request<T>('DELETE', url, config);
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patch<T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<T> {
    return request<T>('PATCH', url, {
      ...config,
      body: JSON.stringify(data),
    });
  },
};

export default fetchRequest;

